import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Optional controls a feature screen can render in the shared header, next to
 * the breadcrumb (e.g. a date-range picker and a "Data View" toggle).
 *
 * The header stays generic: a screen registers what it needs on enter and
 * clears it on leave, so the header never imports feature code or matches on
 * route URLs. Extend this interface (and the header template) when a new shared
 * header control is needed across screens.
 */
export interface HeaderTools {
  /** Show the shared date-range picker with this object-class context. */
  dateRange?: {
    context: number;
    lockPeriod?: boolean;
    timePresetBar?: boolean;
  };
  /** Show a "Data View" toggle bound to the screen's own open state + handler. */
  dataView?: { open$: Observable<boolean>; toggle: () => void };
  /** Show a "Schedule" toggle for the chart above a grid, bound to the screen's own open state + handler (WAP-1990). */
  schedule?: { open$: Observable<boolean>; toggle: () => void };
}

@Injectable({ providedIn: 'root' })
export class HeaderToolsService {
  private readonly tools$ = new BehaviorSubject<HeaderTools | null>(null);

  /** The header subscribes to this to render the active screen's controls. */
  readonly currentTools$ = this.tools$.asObservable();

  /** A screen registers its header controls on enter. */
  set(tools: HeaderTools): void {
    this.tools$.next(tools);
  }

  /** A screen clears its controls on leave (call from the screen's destroy). */
  clear(): void {
    this.tools$.next(null);
  }
}
